import type { ModelProps, ModelRegistry } from '../types';

// ============================================
// WINTERFELL - REAL WORLD (Summer)
// ============================================

export function WinterfellCastleModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'real' ? '#6B6B6B' : '#3a3a4a';
  const stoneDark = world === 'real' ? '#4a4a4a' : '#2a2a3a';
  const roofColor = world === 'real' ? '#4a3a2a' : '#2a2a3a';

  return (
    <group>
      {/* Main keep - central tower */}
      <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.5, 3, 1.5]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Keep battlements */}
      {[[-0.6, 0.6], [-0.6, -0.6], [0.6, 0.6], [0.6, -0.6], [0, 0.6], [0, -0.6]].map(([x, z], i) => (
        <mesh key={i} position={[x, 3.2, z]} castShadow>
          <boxGeometry args={[0.25, 0.4, 0.25]} />
          <meshStandardMaterial color={stoneDark} roughness={0.9} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Side towers */}
      {[[-1.2, -1.2], [1.2, -1.2], [-1.2, 1.2], [1.2, 1.2]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 1.8, 0]} castShadow>
            <cylinderGeometry args={[0.4, 0.5, 3.6, 8]} />
            <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
          </mesh>
          {/* Tower roof */}
          <mesh position={[0, 3.8, 0]} castShadow>
            <coneGeometry args={[0.5, 0.8, 8]} />
            <meshStandardMaterial color={roofColor} roughness={0.85} transparent={isOccluding} opacity={opacity} />
          </mesh>
        </group>
      ))}
      {/* Main entrance */}
      <mesh position={[0, 0.6, 0.8]} castShadow>
        <boxGeometry args={[0.6, 1.2, 0.2]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Arch above entrance */}
      <mesh position={[0, 1.3, 0.78]} castShadow>
        <boxGeometry args={[0.8, 0.15, 0.15]} />
        <meshStandardMaterial color={stoneDark} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Windows */}
      {[[0.4, 2, 0.76], [-0.4, 2, 0.76], [0, 2.4, 0.76]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <boxGeometry args={[0.15, 0.3, 0.05]} />
          <meshStandardMaterial color="#1a1a2a" />
        </mesh>
      ))}
      {/* Stark direwolf banner on front */}
      <mesh position={[0.5, 2.2, 0.77]} rotation={[0, 0, 0]}>
        <planeGeometry args={[0.3, 0.5]} />
        <meshStandardMaterial color={world === 'real' ? '#4a4a4a' : '#3a3a5a'} side={2} />
      </mesh>
    </group>
  );
}

export function RavenTowerModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'real' ? '#5a5a5a' : '#3a3a4a';

  return (
    <group>
      {/* Tower body */}
      <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.5, 0.6, 3, 8]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Conical roof */}
      <mesh position={[0, 3.3, 0]} castShadow>
        <coneGeometry args={[0.65, 1, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#3a3a3a' : '#2a2a3a'} roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Window openings for ravens */}
      {[0, Math.PI/2, Math.PI, Math.PI*1.5].map((angle, i) => (
        <mesh key={i} position={[Math.cos(angle) * 0.51, 2.5, Math.sin(angle) * 0.51]} rotation={[0, -angle, 0]}>
          <boxGeometry args={[0.2, 0.3, 0.1]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
      {/* Ravens perched */}
      {[0.3, -0.3].map((x, i) => (
        <mesh key={i} position={[x, 3.1, 0.4]} castShadow>
          <sphereGeometry args={[0.08, 8, 8]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
    </group>
  );
}

export function WeirwoodTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const barkColor = world === 'real' ? '#f5f5f5' : '#c0c0d0';
  const leafColor = world === 'real' ? '#8B0000' : '#5a2a3a';

  return (
    <group>
      {/* White trunk - gnarled */}
      <mesh position={[0, 1, 0]} castShadow>
        <cylinderGeometry args={[0.3, 0.5, 2, 8]} />
        <meshStandardMaterial color={barkColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Trunk twist */}
      <mesh position={[0.1, 1.8, 0.1]} rotation={[0.1, 0.3, 0.05]} castShadow>
        <cylinderGeometry args={[0.2, 0.3, 1.2, 7]} />
        <meshStandardMaterial color={barkColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Exposed roots */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i / 5) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.4, 0.15, Math.sin(angle) * 0.4]} rotation={[Math.PI/4, angle, 0]} castShadow>
            <cylinderGeometry args={[0.05, 0.1, 0.5, 5]} />
            <meshStandardMaterial color={barkColor} roughness={0.85} transparent={isOccluding} opacity={opacity} />
          </mesh>
        );
      })}
      {/* Carved face - eyes */}
      <mesh position={[-0.1, 1.2, 0.31]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color="#8B0000" emissive="#8B0000" emissiveIntensity={world === 'spectral' ? 0.8 : 0.3} />
      </mesh>
      <mesh position={[0.1, 1.2, 0.31]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color="#8B0000" emissive="#8B0000" emissiveIntensity={world === 'spectral' ? 0.8 : 0.3} />
      </mesh>
      {/* Carved mouth */}
      <mesh position={[0, 0.95, 0.32]}>
        <boxGeometry args={[0.15, 0.08, 0.05]} />
        <meshStandardMaterial color="#4a0000" />
      </mesh>
      {/* Red leaves canopy */}
      <mesh position={[0, 2.8, 0]} castShadow>
        <dodecahedronGeometry args={[1, 1]} />
        <meshStandardMaterial color={leafColor} roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.5, 2.4, 0.3]} castShadow>
        <icosahedronGeometry args={[0.5, 1]} />
        <meshStandardMaterial color={leafColor} roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.4, 2.5, -0.2]} castShadow>
        <icosahedronGeometry args={[0.45, 1]} />
        <meshStandardMaterial color={leafColor} roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Weeping sap (blood) */}
      {world === 'spectral' && (
        <mesh position={[0, 0.8, 0.35]}>
          <cylinderGeometry args={[0.02, 0.01, 0.3, 6]} />
          <meshStandardMaterial color="#8B0000" emissive="#8B0000" emissiveIntensity={0.5} transparent opacity={0.8} />
        </mesh>
      )}
    </group>
  );
}

export function StarkBannerModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Pole */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 3, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#4a4a4a' : '#3a3a4a'} metalness={0.5} roughness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Banner fabric - grey with direwolf silhouette */}
      <mesh position={[0.4, 2.2, 0]} rotation={[0, 0, 0.1]}>
        <planeGeometry args={[0.8, 1.2]} />
        <meshStandardMaterial color={world === 'real' ? '#4a4a4a' : '#3a3a5a'} side={2} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Direwolf head silhouette */}
      <mesh position={[0.4, 2.3, 0.01]}>
        <circleGeometry args={[0.2, 8]} />
        <meshStandardMaterial color="#e8e8e8" side={2} />
      </mesh>
      {/* Pole top ornament */}
      <mesh position={[0, 3.1, 0]} castShadow>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

export function TrainingYardModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const groundColor = world === 'real' ? '#8B7355' : '#4a4a5a';

  return (
    <group>
      {/* Training ground */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]} receiveShadow>
        <planeGeometry args={[2, 2]} />
        <meshStandardMaterial color={groundColor} roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Wooden practice dummies */}
      {[[-0.5, 0.5], [0.5, -0.3]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          {/* Post */}
          <mesh position={[0, 0.6, 0]} castShadow>
            <cylinderGeometry args={[0.05, 0.07, 1.2, 6]} />
            <meshStandardMaterial color="#6B4423" roughness={0.9} />
          </mesh>
          {/* Cross arm */}
          <mesh position={[0, 0.9, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
            <cylinderGeometry args={[0.03, 0.03, 0.5, 6]} />
            <meshStandardMaterial color="#6B4423" roughness={0.9} />
          </mesh>
          {/* Target */}
          <mesh position={[0, 0.9, 0.08]}>
            <circleGeometry args={[0.12, 12]} />
            <meshStandardMaterial color={world === 'real' ? '#8B0000' : '#5a2a3a'} />
          </mesh>
        </group>
      ))}
      {/* Weapon rack */}
      <mesh position={[0.8, 0.4, 0]} castShadow>
        <boxGeometry args={[0.15, 0.8, 0.6]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Practice swords */}
      {[0.1, -0.1].map((z, i) => (
        <mesh key={i} position={[0.85, 0.5, z]} rotation={[0, 0, 0.3 - i * 0.2]} castShadow>
          <boxGeometry args={[0.03, 0.5, 0.02]} />
          <meshStandardMaterial color="#6B6B6B" metalness={0.3} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

export function ForgeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'real' ? '#5a5a5a' : '#3a3a4a';

  return (
    <group>
      {/* Forge structure */}
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 1, 0.8]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Chimney */}
      <mesh position={[0.3, 1.4, -0.2]} castShadow>
        <boxGeometry args={[0.4, 1.2, 0.4]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Fire pit opening */}
      <mesh position={[0, 0.5, 0.41]}>
        <boxGeometry args={[0.4, 0.4, 0.1]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Fire glow */}
      <mesh position={[0, 0.5, 0.35]}>
        <boxGeometry args={[0.3, 0.3, 0.05]} />
        <meshStandardMaterial color="#FF4500" emissive="#FF4500" emissiveIntensity={0.8} />
      </mesh>
      <pointLight position={[0, 0.5, 0.5]} color="#FF4500" intensity={3} distance={3} />
      {/* Anvil */}
      <mesh position={[-0.5, 0.3, 0.5]} castShadow>
        <boxGeometry args={[0.25, 0.2, 0.15]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[-0.5, 0.15, 0.5]} castShadow>
        <cylinderGeometry args={[0.08, 0.1, 0.3, 6]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.8} />
      </mesh>
      {/* Bellows */}
      <mesh position={[0.5, 0.3, 0.4]} rotation={[0, -0.3, 0]} castShadow>
        <boxGeometry args={[0.3, 0.2, 0.15]} />
        <meshStandardMaterial color="#6B4423" roughness={0.9} />
      </mesh>
    </group>
  );
}

export function KennelsModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const woodColor = world === 'real' ? '#6B4423' : '#4a3a4a';

  return (
    <group>
      {/* Main structure */}
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.5, 1, 0.8]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Sloped roof */}
      <mesh position={[0, 1.15, 0]} rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[1.7, 0.15, 1]} />
        <meshStandardMaterial color={world === 'real' ? '#3a3a3a' : '#2a2a3a'} roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Kennel openings */}
      {[-0.4, 0.4].map((x, i) => (
        <mesh key={i} position={[x, 0.35, 0.41]}>
          <boxGeometry args={[0.35, 0.5, 0.1]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
      {/* Fence posts */}
      {[-0.9, 0, 0.9].map((x, i) => (
        <mesh key={i} position={[x, 0.5, 0.6]} castShadow>
          <boxGeometry args={[0.08, 1, 0.08]} />
          <meshStandardMaterial color={woodColor} roughness={0.9} />
        </mesh>
      ))}
      {/* Fence rails */}
      {[0.25, 0.65].map((y, i) => (
        <mesh key={i} position={[0, y, 0.6]} castShadow>
          <boxGeometry args={[1.8, 0.06, 0.04]} />
          <meshStandardMaterial color={woodColor} roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

export function StoneWallModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'real' ? '#6B6B6B' : '#3a3a4a';

  return (
    <group>
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 1.5, 0.4]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Stone texture bumps */}
      {[[-0.5, 0.5], [0.3, 0.8], [-0.2, 0.3], [0.6, 0.6]].map(([x, y], i) => (
        <mesh key={i} position={[x, y, 0.21]} castShadow>
          <boxGeometry args={[0.3, 0.2, 0.05]} />
          <meshStandardMaterial color={world === 'real' ? '#5a5a5a' : '#2a2a3a'} roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

// ============================================
// WINTERFELL - SPECTRAL WORLD (Winter/White Walkers)
// ============================================

export function TheWallModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const iceColor = world === 'real' ? '#e8f4f8' : '#a0c0d0';

  return (
    <group>
      {/* Massive ice wall */}
      <mesh position={[0, 3, 0]} castShadow receiveShadow>
        <boxGeometry args={[10, 6, 0.8]} />
        <meshStandardMaterial
          color={iceColor}
          roughness={0.2}
          metalness={0.1}
          transparent={isOccluding}
          opacity={opacity}
          emissive={world === 'spectral' ? '#4a6a8a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.2 : 0}
        />
      </mesh>
      {/* Ice texture details */}
      {[[-3, 2], [-1, 4], [1, 3], [3, 2.5], [2, 4.5]].map(([x, y], i) => (
        <mesh key={i} position={[x, y, 0.42]}>
          <boxGeometry args={[0.8, 1.2, 0.05]} />
          <meshStandardMaterial
            color={world === 'spectral' ? '#6a8aa0' : '#d0e8f0'}
            transparent
            opacity={0.6}
          />
        </mesh>
      ))}
      {/* Icicles at top */}
      {[-4, -2, 0, 2, 4].map((x, i) => (
        <mesh key={i} position={[x, 5.7, 0.3]} rotation={[Math.PI, 0, 0]} castShadow>
          <coneGeometry args={[0.15, 0.6, 6]} />
          <meshStandardMaterial color="#d0e8f8" roughness={0.1} transparent opacity={0.9} />
        </mesh>
      ))}
    </group>
  );
}

export function IceThroneModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const iceColor = world === 'spectral' ? '#4a8aff' : '#a0c0d0';

  return (
    <group>
      {/* Throne base */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 0.6, 0.8]} />
        <meshStandardMaterial
          color={iceColor}
          roughness={0.15}
          transparent
          opacity={0.85}
          emissive={world === 'spectral' ? '#2a4a8a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.4 : 0}
        />
      </mesh>
      {/* Seat */}
      <mesh position={[0, 0.75, -0.1]} castShadow>
        <boxGeometry args={[0.9, 0.3, 0.6]} />
        <meshStandardMaterial color={iceColor} roughness={0.15} transparent opacity={0.85} />
      </mesh>
      {/* Back - ice spikes */}
      {[-0.3, 0, 0.3].map((x, i) => (
        <mesh key={i} position={[x, 1.5 + Math.abs(x) * 0.5, -0.35]} castShadow>
          <coneGeometry args={[0.15, 1.5 - Math.abs(x) * 0.5, 6]} />
          <meshStandardMaterial
            color={iceColor}
            roughness={0.1}
            transparent
            opacity={0.8}
            emissive={world === 'spectral' ? '#4a6aaa' : undefined}
            emissiveIntensity={world === 'spectral' ? 0.3 : 0}
          />
        </mesh>
      ))}
      {/* Armrests - ice crystals */}
      {[-0.55, 0.55].map((x, i) => (
        <mesh key={i} position={[x, 0.9, 0]} castShadow>
          <boxGeometry args={[0.15, 0.3, 0.5]} />
          <meshStandardMaterial color={iceColor} roughness={0.15} transparent opacity={0.85} />
        </mesh>
      ))}
      {/* Glow effect */}
      {world === 'spectral' && (
        <pointLight position={[0, 1, 0]} color="#4a8aff" intensity={3} distance={4} />
      )}
    </group>
  );
}

export function IceSpiralModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Spiral pattern - White Walker symbol */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 2;
        const radius = 0.3 + i * 0.1;
        return (
          <mesh
            key={i}
            position={[Math.cos(angle) * radius, 0.1 + i * 0.05, Math.sin(angle) * radius]}
            rotation={[Math.PI/2, 0, angle]}
            castShadow
          >
            <boxGeometry args={[0.3, 0.1, 0.15]} />
            <meshStandardMaterial
              color={world === 'spectral' ? '#4a8aff' : '#a0c0d0'}
              emissive={world === 'spectral' ? '#2a4a8a' : undefined}
              emissiveIntensity={world === 'spectral' ? 0.5 : 0}
              roughness={0.2}
              transparent
              opacity={0.9}
            />
          </mesh>
        );
      })}
      {/* Center marker */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <octahedronGeometry args={[0.2]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#6aaaff' : '#c0d8e8'}
          emissive={world === 'spectral' ? '#4a8aff' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.8 : 0}
        />
      </mesh>
      {world === 'spectral' && (
        <pointLight position={[0, 0.5, 0]} color="#4a8aff" intensity={2} distance={3} />
      )}
    </group>
  );
}

export function IceFormationModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const iceColor = world === 'spectral' ? '#4a8aff' : '#c0d8e8';

  return (
    <group>
      {/* Large ice crystal cluster */}
      <mesh position={[0, 0.6, 0]} rotation={[0, 0, 0.1]} castShadow>
        <octahedronGeometry args={[0.5]} />
        <meshStandardMaterial
          color={iceColor}
          roughness={0.1}
          transparent
          opacity={0.85}
          emissive={world === 'spectral' ? '#2a4a8a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        />
      </mesh>
      <mesh position={[0.3, 0.4, 0.2]} rotation={[0.2, 0.5, 0]} castShadow>
        <octahedronGeometry args={[0.3]} />
        <meshStandardMaterial color={iceColor} roughness={0.1} transparent opacity={0.8} />
      </mesh>
      <mesh position={[-0.2, 0.35, -0.15]} rotation={[-0.1, -0.3, 0.2]} castShadow>
        <octahedronGeometry args={[0.25]} />
        <meshStandardMaterial color={iceColor} roughness={0.1} transparent opacity={0.8} />
      </mesh>
      {world === 'spectral' && (
        <pointLight position={[0, 0.5, 0]} color="#4a8aff" intensity={1.5} distance={2} />
      )}
    </group>
  );
}

export function IceSpikesModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const iceColor = world === 'spectral' ? '#4a8aff' : '#c0d8e8';

  return (
    <group>
      {/* Multiple ice spikes */}
      {[
        { pos: [0, 0.5, 0], height: 1, radius: 0.15 },
        { pos: [0.3, 0.35, 0.2], height: 0.7, radius: 0.1 },
        { pos: [-0.25, 0.4, 0.15], height: 0.8, radius: 0.12 },
        { pos: [0.15, 0.3, -0.2], height: 0.6, radius: 0.08 },
        { pos: [-0.15, 0.25, -0.15], height: 0.5, radius: 0.07 },
      ].map((spike, i) => (
        <mesh key={i} position={spike.pos as [number, number, number]} castShadow>
          <coneGeometry args={[spike.radius, spike.height, 6]} />
          <meshStandardMaterial
            color={iceColor}
            roughness={0.1}
            transparent
            opacity={0.85}
            emissive={world === 'spectral' ? '#2a4a8a' : undefined}
            emissiveIntensity={world === 'spectral' ? 0.3 : 0}
          />
        </mesh>
      ))}
    </group>
  );
}

export function FrozenTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const trunkColor = world === 'spectral' ? '#4a5a6a' : '#5a5a5a';
  const iceColor = world === 'spectral' ? '#6aaaff' : '#c0d8e8';

  return (
    <group>
      {/* Dead trunk */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.2, 1.6, 6]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Dead branches */}
      {[
        { pos: [0.2, 1.2, 0], rot: [0, 0, 0.8] },
        { pos: [-0.15, 1.4, 0.1], rot: [0.2, 0, -0.7] },
        { pos: [0.1, 1.0, -0.15], rot: [-0.3, 0, 0.6] },
      ].map((branch, i) => (
        <mesh key={i} position={branch.pos as [number, number, number]} rotation={branch.rot as [number, number, number]} castShadow>
          <cylinderGeometry args={[0.02, 0.04, 0.4, 5]} />
          <meshStandardMaterial color={trunkColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Ice coating */}
      <mesh position={[0, 0.8, 0.11]} castShadow>
        <cylinderGeometry args={[0.12, 0.22, 1.6, 6]} />
        <meshStandardMaterial
          color={iceColor}
          roughness={0.1}
          transparent
          opacity={0.4}
          emissive={world === 'spectral' ? '#4a6a8a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.2 : 0}
        />
      </mesh>
      {/* Icicles */}
      {[0.15, -0.1, 0.2].map((x, i) => (
        <mesh key={i} position={[x, 1.0 - i * 0.15, 0.1]} rotation={[Math.PI, 0, 0]} castShadow>
          <coneGeometry args={[0.03, 0.15, 5]} />
          <meshStandardMaterial color={iceColor} roughness={0.1} transparent opacity={0.8} />
        </mesh>
      ))}
    </group>
  );
}

export function CryptEntranceModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'spectral' ? '#3a3a4a' : '#4a4a4a';

  return (
    <group>
      {/* Stone archway */}
      <mesh position={[-0.5, 0.9, 0]} castShadow>
        <boxGeometry args={[0.2, 1.8, 0.5]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.5, 0.9, 0]} castShadow>
        <boxGeometry args={[0.2, 1.8, 0.5]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.9, 0]} castShadow>
        <boxGeometry args={[1.2, 0.2, 0.5]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Dark entrance */}
      <mesh position={[0, 0.75, 0.1]}>
        <boxGeometry args={[0.8, 1.5, 0.1]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      {/* Steps going down */}
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[0, -0.1 - i * 0.15, 0.3 + i * 0.2]} castShadow>
          <boxGeometry args={[0.7, 0.1, 0.2]} />
          <meshStandardMaterial color={stoneColor} roughness={0.9} />
        </mesh>
      ))}
      {/* Eerie glow from within */}
      {world === 'spectral' && (
        <pointLight position={[0, 0.5, -0.5]} color="#4a8aff" intensity={2} distance={3} />
      )}
    </group>
  );
}

export function ValyrianSwordModel({ world }: ModelProps) {
  return (
    <group rotation={[0.3, 0.8, 0]}>
      {/* Blade */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.05, 0.8, 0.01]} />
        <meshStandardMaterial
          color={world === 'real' ? '#c0c0c0' : '#8a8aaa'}
          metalness={0.9}
          roughness={0.1}
          emissive={world === 'spectral' ? '#6a6a8a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        />
      </mesh>
      {/* Fuller (blood groove) */}
      <mesh position={[0, 0.4, 0.008]}>
        <boxGeometry args={[0.02, 0.6, 0.005]} />
        <meshStandardMaterial color={world === 'real' ? '#a0a0a0' : '#7a7a9a'} metalness={0.8} />
      </mesh>
      {/* Crossguard */}
      <mesh position={[0, 0.02, 0]} castShadow>
        <boxGeometry args={[0.2, 0.04, 0.03]} />
        <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a3a4a'} metalness={0.5} />
      </mesh>
      {/* Handle */}
      <mesh position={[0, -0.12, 0]} castShadow>
        <cylinderGeometry args={[0.025, 0.025, 0.2, 8]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.8} />
      </mesh>
      {/* Pommel - wolf head */}
      <mesh position={[0, -0.25, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#c0c0c0' : '#8a8aaa'} metalness={0.8} roughness={0.2} />
      </mesh>
    </group>
  );
}

export function DragonglassModel({ world }: ModelProps) {
  return (
    <group>
      {/* Obsidian dagger blade */}
      <mesh position={[0, 0.15, 0]} rotation={[0, 0, 0.2]} castShadow>
        <coneGeometry args={[0.04, 0.3, 4]} />
        <meshStandardMaterial
          color="#1a1a1a"
          roughness={0.1}
          metalness={0.3}
          emissive={world === 'spectral' ? '#2a2a4a' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.3 : 0}
        />
      </mesh>
      {/* Handle */}
      <mesh position={[0, -0.02, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.025, 0.1, 6]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.9} />
      </mesh>
      {/* Leather wrap */}
      <mesh position={[0, -0.02, 0]}>
        <torusGeometry args={[0.022, 0.005, 6, 12]} />
        <meshStandardMaterial color="#5a4a3a" />
      </mesh>
    </group>
  );
}

// Registry of all Game of Thrones models
export const gameOfThronesModels: ModelRegistry = {
  // Winterfell Real World
  winterfell_castle: WinterfellCastleModel,
  raven_tower: RavenTowerModel,
  weirwood_tree: WeirwoodTreeModel,
  stark_banner: StarkBannerModel,
  training_yard: TrainingYardModel,
  forge: ForgeModel,
  kennels: KennelsModel,
  stone_wall: StoneWallModel,
  // Winterfell Spectral World (Winter)
  the_wall: TheWallModel,
  ice_throne: IceThroneModel,
  ice_spiral: IceSpiralModel,
  ice_formation: IceFormationModel,
  ice_spikes: IceSpikesModel,
  frozen_tree: FrozenTreeModel,
  crypt_entrance: CryptEntranceModel,
  // Collectibles
  valyrian_sword: ValyrianSwordModel,
  dragonglass: DragonglassModel,
};
